import { type ReactElement, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProjectSettingsViewModel } from '@/features/projects/hooks/useProjectSettingsViewModel';
import { UnauthorizedScreen } from '@/pages/UnauthorizedScreen';
import { useOrganisationEntitlements } from '@/shared/hooks';
import { iconSizes } from '@/theme/tokens';
import { Box, CircularProgress, Stack, Text } from '@/ui';
import { PageStateScaffold } from '@/ui/composites/scaffolds';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { ProjectSettingsContent } from './ProjectSettingsContent';
import type { AuthSession } from '@/auth/types';

type ProjectSettingsPageProps = { session: AuthSession | null };

export const ProjectSettingsPage = ({ session }: ProjectSettingsPageProps): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setOverrideItems, clearOverride } = useBreadcrumbs();
  const {
    projectQuery,
    project,
    updateProject,
    rotateProjectApiKey,
    deleteProject,
    testProjectPipelineWebhook,
    wipeProjectData,
    selectedOrganisationId,
    selectedProjectId,
    canManageProject,
    canManageProjectLoading,
    nameValue,
    setNameValue,
    nameDirty,
    setNameDirty,
    badgeEnabledValue,
    setBadgeEnabledValue,
    badgeCharsValue,
    setBadgeCharsValue,
    badgeColorValue,
    setBadgeColorValue,
    apiKeyValue,
    setApiKeyValue,
    revokeOpen,
    setRevokeOpen,
    mutationError,
    apiKeyError,
    suiteThresholdDrafts,
    setSuiteThresholdDrafts,
    suitePreferredUnitsRef,
    resetSuiteThresholdDrafts,
    suiteThresholdsDirty,
    suiteThresholdsValid,
    testThresholdDrafts,
    setTestThresholdDrafts,
    testPreferredUnitsRef,
    resetTestThresholdDrafts,
    testThresholdsDirty,
    testThresholdsValid,
    executionRunThresholdDrafts,
    setExecutionRunThresholdDrafts,
    executionRunPreferredUnitsRef,
    resetExecutionRunThresholdDrafts,
    executionRunThresholdsDirty,
    executionRunThresholdsValid,
    pipelineThresholdDrafts,
    setPipelineThresholdDrafts,
    pipelinePreferredUnitsRef,
    resetPipelineThresholdDrafts,
    pipelineThresholdsDirty,
    pipelineThresholdsValid,
    stageThresholdDrafts,
    setStageThresholdDrafts,
    stagePreferredUnitsRef,
    resetStageThresholdDrafts,
    stageThresholdsDirty,
    stageThresholdsValid,
    jobThresholdDrafts,
    setJobThresholdDrafts,
    jobPreferredUnitsRef,
    resetJobThresholdDrafts,
    jobThresholdsDirty,
    jobThresholdsValid,
    notify,
  } = useProjectSettingsViewModel(session);
  const resolvedProject = project ?? null;
  const entitlementsQuery = useOrganisationEntitlements(selectedOrganisationId ?? null);
  const entitlements = entitlementsQuery.data ?? null;
  const hideUploadTokenSettings = false;
  const hidePipelineWebhookSettings =
    entitlements !== null && !entitlements.addOns.operationsEnabled;
  const hidePipelineMetricThresholds =
    entitlements !== null &&
    entitlements.plan.code === 'free' &&
    !entitlements.addOns.operationsEnabled;
  const isProjectSettingsRoute = location.pathname === '/project-settings';

  useEffect(() => {
    if (isProjectSettingsRoute) return;
    clearOverride();
  }, [clearOverride, isProjectSettingsRoute]);

  if (!isProjectSettingsRoute) {
    return <></>;
  }

  useEffect(() => {
    if (!resolvedProject) return;
    setOverrideItems([
      { label: 'Projects', href: '/projects' },
      { label: resolvedProject.name, href: `/projects/${resolvedProject.slug}` },
      { label: 'Settings' },
    ]);
  }, [resolvedProject, setOverrideItems]);

  useEffect(
    () => () => {
      clearOverride();
    },
    [clearOverride],
  );

  const viewModel = {
    projectQuery,
    project,
    updateProject,
    rotateProjectApiKey,
    deleteProject,
    testProjectPipelineWebhook,
    wipeProjectData,
    selectedOrganisationId,
    selectedProjectId,
    canManageProject,
    canManageProjectLoading,
    nameValue,
    setNameValue,
    nameDirty,
    setNameDirty,
    badgeEnabledValue,
    setBadgeEnabledValue,
    badgeCharsValue,
    setBadgeCharsValue,
    badgeColorValue,
    setBadgeColorValue,
    apiKeyValue,
    setApiKeyValue,
    revokeOpen,
    setRevokeOpen,
    mutationError,
    apiKeyError,
    suiteThresholdDrafts,
    setSuiteThresholdDrafts,
    suitePreferredUnitsRef,
    resetSuiteThresholdDrafts,
    suiteThresholdsDirty,
    suiteThresholdsValid,
    testThresholdDrafts,
    setTestThresholdDrafts,
    testPreferredUnitsRef,
    resetTestThresholdDrafts,
    testThresholdsDirty,
    testThresholdsValid,
    executionRunThresholdDrafts,
    setExecutionRunThresholdDrafts,
    executionRunPreferredUnitsRef,
    resetExecutionRunThresholdDrafts,
    executionRunThresholdsDirty,
    executionRunThresholdsValid,
    pipelineThresholdDrafts,
    setPipelineThresholdDrafts,
    pipelinePreferredUnitsRef,
    resetPipelineThresholdDrafts,
    pipelineThresholdsDirty,
    pipelineThresholdsValid,
    stageThresholdDrafts,
    setStageThresholdDrafts,
    stagePreferredUnitsRef,
    resetStageThresholdDrafts,
    stageThresholdsDirty,
    stageThresholdsValid,
    jobThresholdDrafts,
    setJobThresholdDrafts,
    jobPreferredUnitsRef,
    resetJobThresholdDrafts,
    jobThresholdsDirty,
    jobThresholdsValid,
    notify,
    navigate,
  };

  return (
    <PageStateScaffold
      session={session}
      sessionMessage="Sign in to manage project settings."
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      requireProject
      projectMessage="Select a project to manage settings."
      requireAdmin
      isAdmin={canManageProject}
      adminFallback={
        <UnauthorizedScreen
          title="Unauthorised"
          body="You must be an admin to manage project settings."
          backTo="/projects"
          backLabel="Back to projects"
        />
      }
      redirecting={projectQuery.isFetched && resolvedProject === null}
      redirectMessage="Redirecting to projects..."
      isLoading={projectQuery.isPending || canManageProjectLoading}
      loadingFallback={
        <Box
          sx={{
            minHeight: '45vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Stack gap="md" align="center">
            <Text role="body" color="textSecondary" align="center">
              Loading project settings...
            </Text>
            <CircularProgress size={iconSizes.lg} />
          </Stack>
        </Box>
      }
      isError={Boolean(projectQuery.error)}
      error={projectQuery.error}
    >
      <ProjectSettingsContent
        viewModel={viewModel}
        hideUploadTokenSettings={hideUploadTokenSettings}
        hidePipelineWebhookSettings={hidePipelineWebhookSettings}
        hidePipelineMetricThresholds={hidePipelineMetricThresholds}
      />
    </PageStateScaffold>
  );
};
